import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Check, Crown, Zap, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store/authStore';
import { ScrollArea } from '../ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PlanSelectionPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuthStore();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelecting(plan.plan_id);
    
    try {
      if (plan.is_free) {
        // Free plan - activate directly
        const response = await fetch(`${API_URL}/api/subscriptions/activate-free`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ plan_id: plan.plan_id })
        });

        if (response.ok) {
          toast.success(`${plan.display_name} activated! Valid for ${plan.free_trial_days} days.`);
          await checkAuth();
          navigate('/dashboard');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to activate plan');
        }
      } else {
        // Paid plan - go to checkout
        navigate(`/checkout/${plan.plan_id}`);
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setSelecting(null);
    }
  };

  const getPlanIcon = (plan) => {
    if (plan.is_free) return <Zap className="w-8 h-8 text-amber-500" />;
    if (plan.price >= 2000) return <Crown className="w-8 h-8 text-yellow-500" />;
    return <Star className="w-8 h-8 text-blue-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-screen">
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="gap-2"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">Choose Your Plan</h1>
            <p className="text-lg text-muted-foreground">
              Select a plan to get started with YellowGold Pro
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card 
                key={plan.plan_id} 
                className={`relative overflow-hidden transition-all hover:shadow-lg ${
                  plan.is_free ? 'border-amber-500/50' : 'border-primary/30'
                }`}
              >
                {plan.is_free && (
                  <div className="absolute top-0 right-0 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-bl">
                    {plan.free_trial_days} Days Free
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto mb-3">
                    {getPlanIcon(plan)}
                  </div>
                  <CardTitle className="text-xl">{plan.display_name}</CardTitle>
                  <CardDescription>
                    {plan.is_free ? 'Try all features free' : 'Full access to all features'}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Price */}
                  <div className="text-center py-4 border-y border-border/50">
                    {plan.is_free ? (
                      <div className="text-3xl font-bold text-amber-500">FREE</div>
                    ) : (
                      <div>
                        <span className="text-4xl font-bold">₹{plan.price}</span>
                        <span className="text-muted-foreground">/{plan.billing_cycle}</span>
                      </div>
                    )}
                  </div>

                  {/* Limits */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.max_users === -1 ? 'Unlimited' : plan.max_users} Users</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.max_clients === -1 ? 'Unlimited' : plan.max_clients} Clients</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.max_matches === -1 ? 'Unlimited' : plan.max_matches} Matches</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.max_sessions === -1 ? 'Unlimited' : plan.max_sessions} Sessions</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 text-sm pt-2 border-t border-border/50">
                    {plan.feature_reference_commission && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Reference Commission</span>
                      </div>
                    )}
                    {plan.feature_patti && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Patti (Share Book)</span>
                      </div>
                    )}
                    {plan.feature_limit && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Betting Limits</span>
                      </div>
                    )}
                    {plan.feature_monday_settling && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Monday Settling</span>
                      </div>
                    )}
                    {plan.feature_mcx_fo && (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>MCX & F&O Trading</span>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button 
                    className="w-full mt-4"
                    variant={plan.is_free ? 'secondary' : 'default'}
                    onClick={() => handleSelectPlan(plan)}
                    disabled={selecting === plan.plan_id}
                  >
                    {selecting === plan.plan_id ? 'Processing...' : (
                      plan.is_free ? 'Start Free Trial' : 'Subscribe Now'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-muted-foreground">
            <p>All plans include 24/7 support and secure data storage</p>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

export default PlanSelectionPage;
